import React, { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    usePlaidLink,
    PlaidLinkOnSuccess,
    PlaidLinkOnEvent,
    PlaidLinkOnExit,
    PlaidLinkOptions,
} from 'react-plaid-link'
import Steps from '../components/Steps'
import { useInfoPreviewContext } from '../context/InfoPreviewContext'
import { useQuoteContext } from '../context/QuoteContext'
import ApiService from '../services/ApiService'
import { LinkTokenRequest } from '../types'

export default function BankAccount() {
    const [token, setToken] = useState<string | null>(null)
    const [error, setError] = useState<boolean>(false)
    const [processing, setProcessing] = useState<boolean>(false)
    const [showAccountSelection, setShowAccountSelection] =
        useState<boolean>(false)
    const [plaidMetadata, setPlaidMetadata] = useState<any>(null)
    const [selectedAccountId, setSelectedAccountId] = useState<string>('')
    const isOAuthRedirect = window.location.href.includes('?oauth_state_id=')
    const navigate = useNavigate()
    const infoPreview = useInfoPreviewContext()?.infoPreview
    const quote = useQuoteContext()?.quote

    React.useEffect(() => {
        if (isOAuthRedirect) {
            setToken(localStorage.getItem('link_token'))
            return
        }
        const createLinkToken = async () => {
            const response = await ApiService.post<LinkTokenRequest>(
                '/me/bank-accounts/get-link-token'
            )

            setToken(response.link_token)
            localStorage.setItem('link_token', response.link_token)
        }
        createLinkToken()
    }, [])

    const submitBankAccount = async (
        publicToken: string,
        metadata: any,
        selectedAccountId?: string
    ) => {
        try {
            const response = await ApiService.post<LinkTokenRequest>(
                '/me/bank-accounts',
                {
                    public_token: publicToken,
                    metadata: metadata,
                    selectedAccountId: selectedAccountId,
                }
            )

            if (infoPreview?.paymentDisabled) {
                navigate('/payment-notice')
            } else {
                navigate('/quote')
            }
        } catch (error) {
            setError(true)
            setProcessing(false)
        }
    }

    const handleAccountSelection = async () => {
        if (!selectedAccountId || !plaidMetadata) return

        setProcessing(true)
        await submitBankAccount(
            plaidMetadata.publicToken,
            plaidMetadata.metadata,
            selectedAccountId
        )
    }

    const onSuccess = useCallback<PlaidLinkOnSuccess>(
        async (publicToken, metadata) => {
            setProcessing(true)
            setPlaidMetadata({ publicToken, metadata })

            // Check if there are multiple accounts to choose from
            if (metadata.accounts && metadata.accounts.length > 1) {
                setShowAccountSelection(true)
                setProcessing(false)
                return
            }

            // If only one account, proceed directly
            const accountId = metadata.accounts?.[0]?.id
            await submitBankAccount(publicToken, metadata, accountId)
        },
        [infoPreview, navigate]
    )
    const onEvent = useCallback<PlaidLinkOnEvent>((eventName, metadata) => {
        console.log('PLAID EVENT', eventName, metadata)
    }, [])
    const onExit = useCallback<PlaidLinkOnExit>((error, metadata) => {
        console.log('PLAID ERROR', error, metadata)
        setError(true)
    }, [])

    const config: PlaidLinkOptions = {
        token,
        onSuccess,
        onEvent,
        onExit,
    }
    if (isOAuthRedirect) {
        config.receivedRedirectUri = window.location.href
    }

    const {
        open,
        ready,
        // error,
        // exit
    } = usePlaidLink(config)

    React.useEffect(() => {
        if (isOAuthRedirect && ready) {
            open()
        }
    }, [ready, open, isOAuthRedirect])

    if (isOAuthRedirect) {
        return <></>
    }

    if (showAccountSelection && plaidMetadata?.metadata?.accounts) {
        return (
            <>
                <Steps active={1} />
                <div className="article" style={{ textAlign: 'center' }}>
                    <img
                        style={{ width: '170px', margin: '0 0 10px 0' }}
                        alt=""
                        src="/assets/lighthouse-plaid.png"
                    />
                    <h3>Select Your Main Account</h3>
                    <p>
                        We found multiple accounts connected to{' '}
                        {plaidMetadata.metadata.institution?.name ||
                            'your bank'}
                        .<br />
                        Please select the account you'd like to use for
                        payments.
                    </p>

                    <div
                        style={{
                            textAlign: 'left',
                            maxWidth: '400px',
                            margin: '20px auto',
                        }}
                    >
                        {plaidMetadata.metadata.accounts.map((account: any) => (
                            <label
                                key={account.id}
                                style={{
                                    display: 'block',
                                    margin: '10px 0',
                                    padding: '15px',
                                    border: '1px solid #ddd',
                                    borderRadius: '5px',
                                    cursor: 'pointer',
                                    backgroundColor:
                                        selectedAccountId === account.id
                                            ? '#f0f8ff'
                                            : '#fff',
                                }}
                            >
                                <input
                                    type="radio"
                                    name="account"
                                    value={account.id}
                                    checked={selectedAccountId === account.id}
                                    onChange={(e) =>
                                        setSelectedAccountId(e.target.value)
                                    }
                                    style={{ marginRight: '10px' }}
                                />
                                <strong>{account.name}</strong>
                                <br />
                                <span
                                    style={{
                                        color: '#666',
                                        fontSize: '0.9rem',
                                    }}
                                >
                                    {account.subtype} - ****{account.mask}
                                </span>
                            </label>
                        ))}
                    </div>

                    {error && (
                        <p style={{ color: '#c00', fontSize: '0.9rem' }}>
                            There was an error connecting your account. Please
                            try again.
                        </p>
                    )}
                </div>
                <p style={{ textAlign: 'center' }}>
                    <button
                        className="button"
                        onClick={handleAccountSelection}
                        disabled={!selectedAccountId || processing}
                    >
                        {processing
                            ? 'Please wait...'
                            : 'Continue with Selected Account'}
                    </button>
                </p>
            </>
        )
    }

    return (
        <>
            <Steps active={1} />
            <div className="article" style={{ textAlign: 'center' }}>
                <img
                    style={{ width: '170px', margin: '0 0 10px 0' }}
                    alt=""
                    src="/assets/lighthouse-plaid.png"
                />
                <h3>Unlock Your Personalized Quote with Plaid</h3>
                <p>
                    Lighthouse has partnered with Plaid, an industry-leader in
                    financial technology, to provide you with a personalized
                    quote and a convenient, secure method of payment.
                </p>
                <p>
                    Your login credentials are never shared with us, and we only
                    access the data necessary to verify your identity, generate
                    an instant quote, and initiate payments ensuring you get the
                    coverage you need quickly, securely, and at a fair price!
                </p>
                <div className="items">
                    <div className="item">
                        <h4>Personalized Offer</h4>
                        <img alt="" src="/assets/icons/people.png" />
                    </div>
                    <div className="item">
                        <h4>Data Encryption</h4>
                        <img alt="" src="/assets/icons/verified.png" />
                    </div>
                    <div className="item">
                        <h4>Seamless Setup</h4>
                        <img alt="" src="/assets/icons/tap.png" />
                    </div>
                    <div className="item">
                        <h4>Trusted by Millions</h4>
                        <img alt="" src="/assets/icons/badge.png" />
                    </div>
                </div>
                {error && (
                    <p style={{ color: '#c00', fontSize: '0.9rem' }}>
                        There was an error connecting your account. Please try
                        again.
                        <br />
                    </p>
                )}
            </div>
            <p style={{ textAlign: 'center' }}>
                <button
                    className="button"
                    onClick={() => {
                        setError(false)
                        open()
                    }}
                    disabled={!ready || processing}
                >
                    {processing ? 'Please wait...' : 'Connect'}
                </button>
            </p>
        </>
    )
}
